import {ReactNode, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Avatar, Box, Skeleton, Stack, Typography} from '@mui/material';
import moment from 'moment';
import {LoadingButton} from '@mui/lab';
import {Add} from '@mui/icons-material';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import './occurrence-history.scss';
import {EntityOccurrenceHistory} from '../../../backend/responses/entity-occurrence';
import {CardCollapse} from '../../shared/card-collapse';
import {translations} from '../../../i18n/translations';
import {AffiliatedUser} from '../../../backend/responses/affiliated-user';

export const OccurrenceHistory = ({
  history,
  isDailyCareLogApp = false,
  professionals,
}: {
  history: EntityOccurrenceHistory;
  isDailyCareLogApp?: boolean;
  professionals?: AffiliatedUser[];
}) => {
  const {t} = useTranslation();
  const [itemsNumber, setItemsNumber] = useState(3);
  const [sortedHistory, setSortedHistory] = useState<EntityOccurrenceHistory>([]);

  const headerStyle = {
    background: isDailyCareLogApp ? 'var(--dcm-white-base)' : 'var(--dcm-bgseniormenu)',
    borderRadius: '5px!important',
    maxWidth: '100%',
    boxShadow: isDailyCareLogApp ? '0px 4px 16px 0px rgba(29, 41, 57, 0.1) ' : 'none',
    padding: '10px',
  };

  useEffect(() => {
    setSortedHistory(
      [...history].sort((a, b) => {
        return new Date(b.registeredAt).getTime() - new Date(a.registeredAt).getTime();
      })
    );
  }, [history]);

  return (
    <Stack className="occurrences-history" flexDirection="column" gap={2}>
      <CardCollapse
        cardProps={{
          sx: headerStyle,
        }}
        collapsableHeader={
          <CollapsableHeader
            title={t(translations.care_and_activities.handle_task_modal.history.title)}
          />
        }
        defaultExpand
      >
        {!professionals && (
          <Stack gap={2}>
            <Skeleton animation="wave" variant="rounded" height="20px" />
          </Stack>
        )}
        {professionals && (
          <Stack>
            {sortedHistory.map(
              ({userAuthorId, registeredAt, actionType, markedAsHandled, notes}, index) => {
                const date = moment(registeredAt);
                const find = professionals.find((user) => user.user.id === userAuthorId);
                const name = userAuthorId && find ? find.user.name : '';

                if (index >= itemsNumber) return null;

                return (
                  <Box key={`action-${index}`} className="action">
                    <Avatar
                      className="action-avatar"
                      src={userAuthorId && find?.user.picture}
                      alt={name}
                    />
                    <Stack className="action-description" gap="4px">
                      <Typography className="name">
                        {name ||
                          t(translations.care_and_activities.handle_task_modal.history.someone)}
                      </Typography>
                      <Typography className="description">{`${actionType === 'acknowledgement' ? t(translations.office.professional_caregiver.occurrences.history.acknowledge) : actionType === 'state-update' ? t(translations.office.professional_caregiver.occurrences.history.state_update) : ''} `}</Typography>
                      {actionType === 'state-update' && (
                        <Stack direction="row" className="status">
                          <Typography className="title">{`${t(translations.care_and_activities.handle_task_modal.history.status)}:`}</Typography>
                          <Typography className={`${markedAsHandled ? 'done' : 'not-done'}`}>
                            {markedAsHandled ? <CheckIcon /> : <ClearIcon />}
                            {`${t(translations.office.professional_caregiver.occurrences.status[markedAsHandled ? 'handled' : 'not_handled'])}`}
                          </Typography>
                        </Stack>
                      )}
                      {notes && <Typography className="notes">{notes}</Typography>}
                      {isDailyCareLogApp && (
                        <Box className="date">
                          <Typography>{`${date.format('DD')}/${date.format('MM')} ${t(translations.schedule_field.readable_periodicity.at_hour)} ${date.format('LT')}`}</Typography>
                        </Box>
                      )}
                    </Stack>
                    {!isDailyCareLogApp && (
                      <Box className="date">
                        <Typography>{`${date.format('DD')}/${date.format('MM')} ${t(translations.schedule_field.readable_periodicity.at_hour)} ${date.format('LT')}`}</Typography>
                      </Box>
                    )}
                  </Box>
                );
              }
            )}
            {sortedHistory.length > itemsNumber && (
              <LoadingButton
                color="primary"
                size="small"
                className="load-more-button"
                endIcon={<Add />}
                onClick={() => setItemsNumber(itemsNumber + 3)}
              >
                {t(translations.dynamic_form.feature_fields.autocomplete.load_more)}
              </LoadingButton>
            )}
          </Stack>
        )}
      </CardCollapse>
    </Stack>
  );
};

const CollapsableHeader = ({title, children}: {title: string; children?: ReactNode}) => (
  <Stack
    flexDirection="row"
    alignContent="center"
    alignItems="center"
    justifyContent="space-between"
    gap="2"
    sx={{width: '100%'}}
  >
    <Typography
      sx={{
        fontSize: '18px',
        fontWeight: '500',
        lineHeight: '18px',
        letterSpacing: '0em',
        textAlign: 'left',
        color: 'var(--dcm-gray)',
        maxWidth: '80%',
      }}
      noWrap
    >
      {title}
    </Typography>

    {children}
  </Stack>
);
